// AdminHome — CPanel v2 home dashboard.
// One-shot KPI roll-up from `GET /api/admin/cpanel-overview` plus a
// quick-action grid, alerts panel, recent admin actions feed, and a
// system mini-card. Replaces the previous System-Health-only landing
// (which now lives at /admin/health).

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import {
  Users,
  LineChart,
  Cpu,
  Coins,
  Activity,
  Bell,
  TrendingUp,
  Loader2,
  AlertTriangle,
  Info,
  AlertOctagon,
  Plug,
  Sparkles,
  Megaphone,
  Flag,
  ScrollText,
  Plus,
} from "lucide-react";

function KpiCard({ label, value, sub, Icon, tone = "indigo", testid }) {
  const toneStyles = {
    indigo: "from-indigo-50 to-white text-indigo-700 ring-indigo-100",
    emerald: "from-emerald-50 to-white text-emerald-700 ring-emerald-100",
    amber: "from-amber-50 to-white text-amber-700 ring-amber-100",
    slate: "from-slate-50 to-white text-slate-700 ring-slate-200",
  };
  const cls = toneStyles[tone] || toneStyles.indigo;
  return (
    <div
      data-testid={testid}
      className={`group relative overflow-hidden rounded-2xl border border-slate-200 bg-gradient-to-br p-5 shadow-sm ring-1 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md ${cls}`}
    >
      <div className="absolute -right-6 -top-6 h-24 w-24 rounded-full bg-white/40 blur-2xl transition-opacity group-hover:opacity-100" />
      <div className="relative flex items-center justify-between">
        <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">
          {label}
        </span>
        <Icon className="h-4 w-4 opacity-70" />
      </div>
      <div className="relative mt-3 text-3xl font-bold tabular-nums text-slate-900">{value}</div>
      {sub && <div className="relative mt-1 text-xs text-slate-500">{sub}</div>}
    </div>
  );
}

function MiniStat({ label, value, sub, testid }) {
  return (
    <div data-testid={testid} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">
        {label}
      </div>
      <div className="mt-1.5 text-xl font-bold tabular-nums text-slate-900">{value}</div>
      {sub && <div className="mt-0.5 text-xs text-slate-500">{sub}</div>}
    </div>
  );
}

function alertIcon(level) {
  if (level === "critical") return AlertOctagon;
  if (level === "warning") return AlertTriangle;
  return Info;
}
function alertClasses(level) {
  if (level === "critical") return "border-red-200 bg-red-50 text-red-800";
  if (level === "warning") return "border-amber-200 bg-amber-50 text-amber-800";
  return "border-indigo-200 bg-indigo-50 text-indigo-800";
}

const QUICK_ACTIONS = [
  { to: "/admin/users", label: "Browse users", Icon: Users, tid: "qa-users" },
  { to: "/admin/deals", label: "View deals", Icon: LineChart, tid: "qa-deals" },
  { to: "/admin/integrations", label: "Integrations", Icon: Plug, tid: "qa-integrations" },
  { to: "/admin/announcements", label: "New announcement", Icon: Megaphone, tid: "qa-announce" },
  { to: "/admin/feature-flags", label: "Feature flags", Icon: Flag, tid: "qa-flags" },
  { to: "/admin/audit-log", label: "Audit log", Icon: ScrollText, tid: "qa-audit" },
];

function formatCurrency(v) {
  if (v == null || isNaN(v)) return "$0";
  if (v >= 1000) return `$${(v / 1000).toFixed(1)}k`;
  return `$${v.toFixed(2)}`;
}

function formatTimestamp(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return d.toLocaleDateString();
  } catch {
    return iso;
  }
}

export default function AdminHome() {
  const [overview, setOverview] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.get("/admin/cpanel-overview")
      .then(({ data }) => setOverview(data))
      .catch((e) => setErr(e?.response?.data?.detail || "Could not load overview"));
  }, []);

  if (err) {
    return (
      <div data-testid="admin-home-error" className="rounded-xl border border-red-200 bg-red-50 p-6 text-sm text-red-800">
        {err}
      </div>
    );
  }
  if (!overview) {
    return (
      <div className="flex items-center gap-2 text-sm text-slate-500" data-testid="admin-home-loading">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading CPanel overview…
      </div>
    );
  }

  const { users, deals, ai, revenue, viral, system, recent_admin_actions, alerts } = overview;

  return (
    <div className="space-y-8 fade-in" data-testid="admin-home">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-indigo-600">
            Admin CPanel
          </p>
          <h1 className="mt-1 text-3xl font-bold tracking-tight text-slate-900">
            Operational overview
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Today's KPIs, alerts, and the latest admin activity at a glance.
          </p>
        </div>
        <Link
          to="/admin/health"
          className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 transition-all hover:border-slate-300 hover:shadow-sm"
          data-testid="link-to-health"
        >
          Full system health →
        </Link>
      </div>

      {/* 4 top KPI cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard
          label="Total users"
          value={users.total.toLocaleString()}
          sub={`+${users.new_today} today · +${users.new_7d} this week`}
          Icon={Users}
          tone="indigo"
          testid="kpi-users"
        />
        <KpiCard
          label="Total deals"
          value={deals.total.toLocaleString()}
          sub={`+${deals.new_today} new today`}
          Icon={LineChart}
          tone="emerald"
          testid="kpi-deals"
        />
        <KpiCard
          label="AI cost today"
          value={formatCurrency(ai.cost_today_usd)}
          sub={`${ai.calls_today} call${ai.calls_today === 1 ? "" : "s"} · 7d ${formatCurrency(ai.cost_7d_usd)}`}
          Icon={Cpu}
          tone="amber"
          testid="kpi-ai-cost"
        />
        <KpiCard
          label="MRR estimate"
          value={formatCurrency(revenue.mrr_estimate_usd)}
          sub={`${revenue.pro_users_count} Pro user${revenue.pro_users_count === 1 ? "" : "s"} (est.)`}
          Icon={Coins}
          tone="indigo"
          testid="kpi-mrr"
        />
      </div>

      {/* 3 mini stat cards */}
      <div className="grid gap-4 sm:grid-cols-3">
        <MiniStat
          label="Active today"
          value={users.active_today.toLocaleString()}
          sub="Audit + signup activity"
          testid="ministat-active"
        />
        <MiniStat
          label="Viral signups today"
          value={viral.signups_attributed_today.toLocaleString()}
          sub={`${viral.shares_today} share${viral.shares_today === 1 ? "" : "s"} · ${viral.referrals_active} Pro on referral`}
          testid="ministat-viral"
        />
        <MiniStat
          label="Integrations"
          value={`${system.integrations_healthy_count} / ${system.integrations_total_count}`}
          sub={`DB ${system.db_latency_ms < 0 ? "unreachable" : `${system.db_latency_ms}ms`}`}
          testid="ministat-integrations"
        />
      </div>

      {/* 2-column body */}
      <div className="grid gap-6 lg:grid-cols-[1.45fr_1fr]">
        {/* Left col: alerts + audit feed */}
        <div className="space-y-6">
          <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <header className="mb-4 flex items-center justify-between">
              <h2 className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                <Bell className="h-4 w-4 text-amber-600" /> Alerts
              </h2>
              <span className="text-xs text-slate-500" data-testid="alerts-count">{alerts.length} active</span>
            </header>
            <div className="space-y-2" data-testid="alerts-list">
              {alerts.map((a, i) => {
                const Icon = alertIcon(a.level);
                // Phase 46 Batch D — stable composite key. Alert
                // payloads carry no id, but `level + message` is
                // unique-by-render (and `+ i` disambiguates the
                // theoretical duplicate-string case).
                const key = `${a.level || "info"}-${a.message || ""}-${i}`;
                return (
                  <div
                    key={key}
                    data-testid={`alert-row-${i}`}
                    className={`flex items-start gap-3 rounded-xl border px-4 py-3 text-sm transition-all hover:translate-x-0.5 ${alertClasses(a.level)}`}
                  >
                    <Icon className="mt-0.5 h-4 w-4 flex-shrink-0" />
                    <div className="flex-1">
                      <span className="font-medium">{a.message}</span>
                      {a.action_url && (
                        <Link to={a.action_url} className="ms-2 text-xs font-semibold underline-offset-2 hover:underline">
                          Investigate →
                        </Link>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <header className="mb-4 flex items-center justify-between">
              <h2 className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                <Activity className="h-4 w-4 text-indigo-600" /> Recent admin actions
              </h2>
              <Link
                to="/admin/audit-log"
                className="text-xs font-semibold text-indigo-700 underline-offset-2 hover:underline"
                data-testid="audit-feed-link"
              >
                Full log →
              </Link>
            </header>
            {recent_admin_actions.length === 0 ? (
              <p className="text-sm text-slate-500" data-testid="audit-feed-empty">No admin actions logged yet.</p>
            ) : (
              <ul className="divide-y divide-slate-100" data-testid="audit-feed-list">
                {recent_admin_actions.map((row, i) => (
                  <li
                    key={`${row.action}-${row.created_at}-${i}`}
                    data-testid={`audit-feed-row-${i}`}
                    className="flex items-center justify-between gap-3 py-2.5"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-slate-900">{row.action}</p>
                      <p className="truncate text-xs text-slate-500">
                        {row.actor_email} · {row.target_summary || "—"}
                      </p>
                    </div>
                    <span className="flex-shrink-0 text-xs tabular-nums text-slate-400">
                      {formatTimestamp(row.created_at)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>

        {/* Right col: system mini + quick actions + deal-type breakdown */}
        <div className="space-y-6">
          <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <h2 className="flex items-center gap-2 text-sm font-semibold text-slate-900">
              <Sparkles className="h-4 w-4 text-indigo-600" /> Quick actions
            </h2>
            <div className="mt-4 grid grid-cols-2 gap-2.5">
              {QUICK_ACTIONS.map(({ to, label, Icon, tid }) => (
                <Link
                  key={to}
                  to={to}
                  data-testid={tid}
                  className="group flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm font-medium text-slate-700 transition-all hover:-translate-y-0.5 hover:border-indigo-300 hover:bg-indigo-50 hover:text-indigo-700 hover:shadow-sm"
                >
                  <Icon className="h-4 w-4 text-slate-400 transition-colors group-hover:text-indigo-600" />
                  <span className="truncate">{label}</span>
                </Link>
              ))}
            </div>
          </section>

          <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <h2 className="flex items-center gap-2 text-sm font-semibold text-slate-900">
              <TrendingUp className="h-4 w-4 text-emerald-600" /> Deals by type
            </h2>
            <div className="mt-4 space-y-3" data-testid="deals-by-type">
              {Object.entries(deals.by_type).map(([type, count]) => {
                const total = Object.values(deals.by_type).reduce((s, n) => s + (n || 0), 0) || 1;
                const pct = Math.round(((count || 0) / total) * 100);
                return (
                  <div key={type} data-testid={`deal-type-${type}`}>
                    <div className="flex items-center justify-between text-xs">
                      <span className="font-medium capitalize text-slate-700">{type}</span>
                      <span className="tabular-nums text-slate-500">{count} · {pct}%</span>
                    </div>
                    <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
                      <div
                        className="h-full rounded-full bg-indigo-500 transition-all duration-500"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <h2 className="flex items-center gap-2 text-sm font-semibold text-slate-900">
              <Plus className="h-4 w-4 text-indigo-600" /> Need more?
            </h2>
            <p className="mt-2 text-sm text-slate-600">
              The full system health, AI usage trends, and viral attribution drill-downs are one click away.
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              <Link
                to="/admin/health"
                className="rounded-full bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white transition-all hover:-translate-y-0.5 hover:bg-slate-800 hover:shadow-sm"
                data-testid="link-health"
              >
                System health
              </Link>
              <Link
                to="/admin/ai-calls"
                className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 transition-all hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-sm"
                data-testid="link-ai"
              >
                AI usage
              </Link>
              <Link
                to="/admin/viral-attribution"
                className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 transition-all hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-sm"
                data-testid="link-viral"
              >
                Viral attribution
              </Link>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
